import { z } from 'zod';
import { OBJECT_NAMES } from './objects';

const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

// ---------------------------------------------------------------------------
// SCIM 2.0 provisioning (RFC 7643 / 7644): tokens and settings
// ---------------------------------------------------------------------------

export const scimTokenSchema = z.object({ name: z.string().trim().min(1).max(80) }).strict();
export const scimSettingsSchema = z.object({ permissionSetId: uuid.nullable() }).strict();

export interface ScimToken { id: string; name: string; prefix: string; createdAt: string; lastUsedAt: string | null }
export interface ScimSettings {
  /** Base URL to enter at the identity provider (Tenant URL / SCIM connector base URL). */
  baseUrl: string;
  permissionSetId: string | null;
  tokens: ScimToken[];
  /** Members created or updated by SCIM. */
  provisionedCount: number;
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export interface AuditEvent {
  id: string;
  occurredAt: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  objectName: string | null;
  recordId: string | null;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
  details: Record<string, unknown>;
}

export const auditQuerySchema = z
  .object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    /** Exact action, or a prefix ending in "." (e.g. "auth." or "setup."). */
    action: z.string().trim().max(80).regex(/^[a-z_.]*$/, 'Use an action such as auth.login or a prefix such as setup.').optional(),
    actorId: uuid.optional(),
    objectName: z.enum(OBJECT_NAMES).optional(),
    recordId: uuid.optional(),
    /** Newest first: pass the last id of a page to get the next (older) one. */
    before: uuid.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type AuditQuery = z.infer<typeof auditQuerySchema>;

/** SIEM pull: oldest first, from just after a cursor (event ids are time-ordered UUIDv7). */
export const auditFeedSchema = z
  .object({ after: uuid.optional(), since: z.string().datetime().optional(), limit: z.coerce.number().int().min(1).max(1000).default(500) })
  .strict();

export const AUDIT_CSV_LIMIT = 50_000;

// ---------------------------------------------------------------------------
// Configuration package ("sandbox" copy): an org's setup as JSON, imported into another org
// ---------------------------------------------------------------------------

export const CONFIG_FORMAT = 'crm-config';
export const CONFIG_VERSION = 1;
export const CONFIG_SECTIONS = [
  'roles', 'groups', 'queues', 'customFields', 'picklists', 'layouts', 'pipelines', 'sharing', 'validationRules', 'workflows', 'approvalProcesses',
] as const;
export type ConfigSection = (typeof CONFIG_SECTIONS)[number];

const list = z.array(z.record(z.unknown())).max(2000).default([]);

/**
 * The package is validated loosely here; every item is then checked by the same service code that
 * Setup uses, so an item that doesn't fit the target org is reported and skipped, not half-applied.
 */
export const configPackageSchema = z
  .object({
    format: z.literal(CONFIG_FORMAT),
    version: z.literal(CONFIG_VERSION),
    exportedAt: z.string().max(40),
    organization: z.string().max(200),
    roles: list,
    groups: list,
    queues: list,
    customFields: list,
    picklists: list,
    layouts: list,
    pipelines: list,
    sharing: z.object({ models: z.record(z.string()), hierarchy: z.record(z.boolean()) }).partial().default({}),
    validationRules: list,
    workflows: list,
    approvalProcesses: list,
    /** Ids in the source org → portable keys, so references can be re-pointed in the target org. */
    refs: z
      .object({
        users: z.record(z.string().email()),
        groups: z.record(z.string()),
        queues: z.record(z.string()),
        roles: z.record(z.string()),
        pipelines: z.record(z.string()),
        stages: z.record(z.tuple([z.string(), z.string()])),
      })
      .partial()
      .default({}),
  })
  .strict();
export type ConfigPackage = z.infer<typeof configPackageSchema>;

export const configImportSchema = z
  .object({
    package: configPackageSchema,
    dryRun: z.boolean().default(true),
    sections: z.array(z.enum(CONFIG_SECTIONS)).min(1).default([...CONFIG_SECTIONS]),
  })
  .strict();

export interface ConfigImportResult {
  applied: boolean;
  items: { section: ConfigSection; name: string; action: 'created' | 'updated' | 'unchanged' | 'skipped'; message?: string }[];
}
